import { TFunction } from 'i18next';
import { InvoiceConfig } from '../tenants/config';
import { TenantOrganizationType } from '../api/account/accountApiTypes';

/** One Pflichtangabe of the invoice footer's imprint column. */
export type InvoiceFooterField =
    | 'taxRegistration'
    | 'seat'
    | 'register'
    | 'managingDirectors'
    | 'representative'
    | 'supervisoryBoardChair';

/**
 * Which Pflichtangaben the admin UI's own invoice prints, by the tenant's legal
 * form, in print order (#3430; PO, 2026-10-30: the client-side invoice follows
 * the same rule as the issued one).
 *
 * COPY of `SELLER_FOOTER_FIELDS` in
 * `new-backend/admin/src/lib/invoicing/pdfkitRenderer.ts`, which is the rule's
 * home. admin-ui cannot import new-backend, and does not consume `@kolula/shared`
 * yet (`docs/plans/shared-rules-package.md` › Open questions), so the table is
 * copied and the two are pinned by `invoiceLegalLines.test.ts`, which reads the
 * server table and fails on any difference. Change both in the same PR.
 *
 * - **company**: tax line · Sitz · Handelsregister · Geschäftsführung ·
 *   Aufsichtsratsvorsitz.
 * - **municipality**, **association**: tax line · the representative under the
 *   neutral label "Vertretungsberechtigte Person". No register, no Sitz.
 * - **unknown** (a bundled legacy config, a form not loaded, a value outside
 *   the enum): exactly the lines this invoice printed before.
 *
 * Name and address are not part of it: the footer's first column prints them
 * for every form. A line prints only when its value is filled.
 */
export const INVOICE_FOOTER_FIELDS: Record<TenantOrganizationType | 'unknown', readonly InvoiceFooterField[]> = {
    company: ['taxRegistration', 'seat', 'register', 'managingDirectors', 'supervisoryBoardChair'],
    municipality: ['taxRegistration', 'representative'],
    association: ['taxRegistration', 'representative'],
    unknown: ['taxRegistration', 'register', 'managingDirectors', 'supervisoryBoardChair'],
};

const formOf = (value: string | null | undefined): TenantOrganizationType | 'unknown' => {
    const v = value?.trim().toLowerCase();
    return v && Object.prototype.hasOwnProperty.call(INVOICE_FOOTER_FIELDS, v)
        ? (v as TenantOrganizationType | 'unknown')
        : 'unknown';
};

/**
 * The imprint column of the footer: the lines `INVOICE_FOOTER_FIELDS` selects
 * for the tenant's legal form, each only when its value is filled. The labels
 * are this invoice's own (`invoice.*`), as before.
 */
export const invoiceLegalLines = (config: InvoiceConfig, t: TFunction): string[] => {
    const filled = (v: string | null | undefined): string | null => v?.trim() || null;
    const taxId = filled(config.taxId);
    const city = filled(config.companyCity);
    const register = filled(config.companyRegistration);
    const directors = filled(config.managingDirectors);
    const chair = filled(config.supervisoryBoardChair);
    const taxIdLabelKey = config.taxIdKind === 'tax_number' ? 'invoice.tax_number' : 'invoice.vat_id';
    const line: Record<InvoiceFooterField, () => string | null> = {
        taxRegistration: () => (taxId ? `${t(taxIdLabelKey)}: ${taxId}` : null),
        seat: () => (city ? `${t('invoice.seat')}: ${city}` : null),
        register: () => (register ? `${t('invoice.registration')}: ${register}` : null),
        managingDirectors: () => (directors ? `${t('invoice.managing_directors')}: ${directors}` : null),
        representative: () => (directors ? `${t('invoice.representative')}: ${directors}` : null),
        supervisoryBoardChair: () => (chair ? `${t('invoice.supervisory_board_chair')}: ${chair}` : null),
    };
    return INVOICE_FOOTER_FIELDS[formOf(config.legalForm)]
        .map((field) => line[field]())
        .filter((l): l is string => l !== null);
};
